import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { PresaveCampaignStatus } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { openExternal } from '../../../utils/openExternal';
import { formatReleaseDay } from './presaveDates';
import { PanelFoot } from '../../music24/components/primitives/Panel';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresavePanelFooterProps {
  status: PresaveCampaignStatus;
  releaseDate: string;
  shareUrl: string | null;
  /**
   * The song of the campaign. The actions name it, so a page with several songs (a Bio Link) says
   * which pre-save they end (QA 2026-10-28, brk-r2-5). Empty: the general labels.
   */
  songTitle?: string;
  openReleaseModal: () => void;
  openCancelModal: () => void;
}

/**
 * The two actions that end a campaign, demoted to the footer next to the
 * sentence that explains them. They used to be a full-width violet button and
 * a violet link — the loudest things on the panel, and both irreversible.
 */
const PresavePanelFooterComponent: React.FC<PresavePanelFooterProps> = ({
  status,
  releaseDate,
  shareUrl,
  songTitle = '',
  openReleaseModal,
  openCancelModal,
}) => {
  const intl = useIntl();
  const withLang = usePreviewPageLang();
  const title = songTitle.trim();
  const hasTitle = title !== '';

  const openPage = useCallback(() => {
    if (shareUrl === null) {
      return;
    }
    openExternal(withLang(shareUrl)).catch(console.error);
  }, [shareUrl, withLang]);

  const openPageButton =
    shareUrl === null ? null : (
      <SMLButton size="sm" outline onClick={openPage}>
        {intl.formatMessage({ id: 'app.sml.presave.panel.open_page', defaultMessage: 'Open page' })}
      </SMLButton>
    );

  if (status === PresaveCampaignStatus.scheduled) {
    return (
      <PanelFoot>
        <p className={styles.footerNote}>
          {intl.formatMessage(
            {
              id: 'app.sml.presave.panel.footer_scheduled_v2',
              defaultMessage:
                'Your song comes out on {date}. On that day Music24 finds it on the services and updates this page. Press "My song is out now" only if the song is out earlier.',
            },
            { date: formatReleaseDay(intl, releaseDate) }
          )}
        </p>
        <span className={styles.footerActions}>
          {/* omitCap: the title case drops the dash and the hyphen of these labels. */}
          <SMLButton size="sm" outline omitCap onClick={openReleaseModal}>
            {hasTitle
              ? intl.formatMessage(
                  {
                    id: 'app.sml.presave.panel.song_out_now_titled',
                    defaultMessage: '{title} is out now – update the page',
                  },
                  { title }
                )
              : intl.formatMessage({
                  id: 'app.sml.presave.panel.song_out_now',
                  defaultMessage: 'My song is out now – update the page',
                })}
          </SMLButton>
          <SMLButton size="sm" outline omitCap color="danger" onClick={openCancelModal}>
            {hasTitle
              ? intl.formatMessage(
                  { id: 'app.sml.presave.panel.stop_presave_titled', defaultMessage: 'Stop the pre-save of {title}' },
                  { title }
                )
              : intl.formatMessage({ id: 'app.sml.presave.panel.stop_presave', defaultMessage: 'Stop the pre-save' })}
          </SMLButton>
        </span>
      </PanelFoot>
    );
  }

  if (status === PresaveCampaignStatus.released) {
    return (
      <PanelFoot>
        <p className={styles.footerNote}>
          {intl.formatMessage({
            id: 'app.sml.presave.panel.footer_released',
            defaultMessage: 'Campaign closed. The link stays live and keeps routing fans to the release.',
          })}
        </p>
        <span className={styles.footerActions}>{openPageButton}</span>
      </PanelFoot>
    );
  }

  return (
    <PanelFoot>
      <p className={styles.footerNote}>
        {status === PresaveCampaignStatus.cancelled
          ? intl.formatMessage({
              id: 'app.sml.presave.panel.footer_cancelled',
              defaultMessage:
                'Campaign called off. The page stays online, the block no longer takes sign-ups, and the fans who signed up are kept.',
            })
          : intl.formatMessage({
              id: 'app.sml.presave.panel.footer_expired',
              defaultMessage:
                'The release day passed without the campaign being marked released, so nothing was sent. The fans who signed up are kept.',
            })}
      </p>
      <span className={styles.footerActions}>{openPageButton}</span>
    </PanelFoot>
  );
};

export const PresavePanelFooter = TypedMemo(PresavePanelFooterComponent);
